// src/components/charts/SignalChart.jsx
import React, { useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import { getPrimaryColour } from "../../utils/getPrimaryColour";
import { useTranslation } from "react-i18next";

export function SignalChart({
  rawSignal = [],
  smoothSignal = [],
  peaks = [],
  troughs = [],
  // Detected breaths to shade as background bands: [{ startSec, endSec, rejected }].
  breaths = [],
  spirometrySummary,
  selectedBreathIndex = null,
  showSpirometryMarkers = false, // default OFF
  showHighlight = false, // default OFF
  highlightFromIndex = null, // NEW
  highlightToIndex = null, // NEW
  maxSecs = 60,
  height,
  tickStepSize = 5,
  logContext = null,
}) {
  const { t, i18n } = useTranslation();
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  // Read by the inline band plugin so it always draws the latest breaths.
  const breathsRef = useRef(breaths);
  breathsRef.current = breaths;

  // init chart
  useEffect(() => {
    if (!chartRef.current) return;

    const ctx = chartRef.current.getContext("2d");
    const primary = getPrimaryColour();

    // Shade each detected breath as a tertiary-coloured background band.
    const breathBandsPlugin = {
      id: "breathBands",
      beforeDatasetsDraw(chart) {
        const bands = breathsRef.current;
        if (!Array.isArray(bands) || !bands.length) return;
        const xScale = chart.scales.x;
        const area = chart.chartArea;
        if (!xScale || !area) return;
        const g = chart.ctx;
        g.save();
        for (const b of bands) {
          if (!Number.isFinite(b?.startSec) || !Number.isFinite(b?.endSec))
            continue;
          const x1 = xScale.getPixelForValue(b.startSec);
          const x2 = xScale.getPixelForValue(b.endSec);
          const left = Math.max(area.left, Math.min(x1, x2));
          const right = Math.min(area.right, Math.max(x1, x2));
          if (right <= left) continue;
          // tertiary (#B776B9); rejected breaths shaded fainter/grey.
          g.fillStyle = b.rejected
            ? "rgba(147,147,147,0.10)"
            : "rgba(183,118,185,0.18)";
          g.fillRect(left, area.top, right - left, area.bottom - area.top);
        }
        g.restore();
      },
    };

    chartInstanceRef.current = new Chart(ctx, {
      type: "line",
      plugins: [breathBandsPlugin],
      data: {
        datasets: [
          // 0: RAW (hidden)
          {
            label: "Raw intensity",
            data: [],
            borderWidth: 1,
            pointRadius: 0,
            tension: 0,
            borderColor: "rgba(150,150,150,0.4)",
            hidden: true,
          },
          // 1: SMOOTHED (Main signal)
          {
            label: "Smoothed",
            data: [],
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.2,
            borderColor: primary,
          },
          // 2: PEAKS
          {
            label: "Peaks",
            type: "scatter",
            data: [],
            pointRadius: 3,
            showLine: false,
            borderColor: "rgb(255,105,180)",
            backgroundColor: "rgb(255,105,180)",
          },
          // 3: TROUGHS
          {
            label: "Troughs",
            type: "scatter",
            data: [],
            pointRadius: 3,
            showLine: false,
            borderColor: "rgb(255,215,0)",
            backgroundColor: "rgb(255,215,0)",
          },
          // 4: FEV1 marker
          {
            label: "FEV1",
            type: "scatter",
            data: [],
            pointRadius: 4,
            showLine: false,
            borderColor: "rgb(0,150,255)",
            backgroundColor: "rgb(0,150,255)",
            hidden: !showSpirometryMarkers,
          },
          // 5: FVC marker
          {
            label: "FVC",
            type: "scatter",
            data: [],
            pointRadius: 4,
            showLine: false,
            borderColor: "rgb(138,43,226)",
            backgroundColor: "rgb(138,43,226)",
            hidden: !showSpirometryMarkers,
          },
          // 6: HIGHLIGHT OVERLAY
          {
            label: "Selected Breath",
            data: [],
            // borderWidth: 4,
            pointRadius: 0,
            tension: 0.2,
            // borderColor: "rgba(72, 46, 73, 0.8)",
            backgroundColor: "rgba(6, 48, 57, 0.1)",
            fill: true,
            hidden: !showHighlight,
          },
        ],
      },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: "linear",
            min: 0,
            max: maxSecs,
            ticks: { stepSize: tickStepSize },
            title: {
              display: true,
              text: t("charts.signal.timeSeconds", "Time (s)"),
            },
          },
          y: {
            beginAtZero: false,
            ticks: {
              display: false,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
        },
      },
    });

    return () => {
      if (chartInstanceRef.current) chartInstanceRef.current.destroy();
    };
  }, [
    showSpirometryMarkers,
    showHighlight,
    tickStepSize,
    maxSecs,
    t,
    i18n.language,
  ]);

  // update chart data
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (!chart) return;

    // Base data mapping
    chart.data.datasets[0].data = rawSignal.map((s) => ({
      x: s.t,
      y: s.value,
    }));
    chart.data.datasets[1].data = smoothSignal.map((s) => ({
      x: s.t,
      y: s.value,
    }));
    chart.data.datasets[2].data = peaks.map((p) => ({ x: p.t, y: p.value }));
    chart.data.datasets[3].data = troughs.map((p) => ({ x: p.t, y: p.value }));

    // Spirometry markers
    let fev1Point = null;
    let fvcPoint = null;

    if (showSpirometryMarkers && spirometrySummary && smoothSignal.length) {
      const { fev1Time, trough } = spirometrySummary;
      const fevSample = smoothSignal.reduce((best, s) => {
        const bestDt = Math.abs(best.t - fev1Time);
        const curDt = Math.abs(s.t - fev1Time);
        return curDt < bestDt ? s : best;
      }, smoothSignal[0]);

      fev1Point = { x: fevSample.t, y: fevSample.value };
      if (trough) {
        fvcPoint = { x: trough.t, y: trough.value };
      }
    }

    chart.data.datasets[4].data = fev1Point ? [fev1Point] : [];
    chart.data.datasets[5].data = fvcPoint ? [fvcPoint] : [];
    chart.data.datasets[4].hidden = !showSpirometryMarkers;
    chart.data.datasets[5].hidden = !showSpirometryMarkers;

    // Highlight selected breath OR selected range
    let highlightData = [];

    if (
      troughs &&
      troughs.length > 1 &&
      smoothSignal.length > 0 &&
      (showHighlight ||
        (highlightFromIndex != null && highlightToIndex != null))
    ) {
      const sortedTroughs = [...troughs].sort((a, b) => a.index - b.index);

      // Prefer explicit range if provided
      if (
        highlightFromIndex != null &&
        highlightToIndex != null &&
        highlightFromIndex >= 0 &&
        highlightToIndex > highlightFromIndex &&
        highlightToIndex < sortedTroughs.length
      ) {
        const startIdx = sortedTroughs[highlightFromIndex].index;
        const endIdx = sortedTroughs[highlightToIndex].index;

        if (startIdx < smoothSignal.length && endIdx < smoothSignal.length) {
          const s = Math.min(startIdx, endIdx);
          const e = Math.max(startIdx, endIdx);
          highlightData = smoothSignal.slice(s, e + 1).map((pt) => ({
            x: pt.t,
            y: pt.value,
          }));
        }
      } else if (
        // fallback: old single-breath mode
        showHighlight &&
        selectedBreathIndex !== null &&
        selectedBreathIndex >= 0 &&
        selectedBreathIndex < sortedTroughs.length
      ) {
        const startI = sortedTroughs[selectedBreathIndex].index;
        const endI =
          selectedBreathIndex === sortedTroughs.length - 1
            ? smoothSignal.length - 1
            : sortedTroughs[selectedBreathIndex + 1].index;

        if (startI < smoothSignal.length && endI < smoothSignal.length) {
          const s = Math.min(startI, endI);
          const e = Math.max(startI, endI);
          highlightData = smoothSignal.slice(s, e + 1).map((pt) => ({
            x: pt.t,
            y: pt.value,
          }));
        }
      }
    }

    chart.data.datasets[6].data = highlightData;
    chart.data.datasets[6].hidden = !highlightData.length;

    chart.options.scales.x.max = maxSecs;

    if (process.env.NODE_ENV !== "test") {
      const smoothLastT =
        smoothSignal.length > 0 &&
        Number.isFinite(smoothSignal[smoothSignal.length - 1]?.t)
          ? smoothSignal[smoothSignal.length - 1].t
          : null;
      const rawLastT =
        rawSignal.length > 0 && Number.isFinite(rawSignal[rawSignal.length - 1]?.t)
          ? rawSignal[rawSignal.length - 1].t
          : null;

      console.log("[result signal] chart updated", {
        ...(logContext && typeof logContext === "object" ? logContext : {}),
        rawPointCount: rawSignal.length,
        smoothPointCount: smoothSignal.length,
        peaksCount: peaks.length,
        troughsCount: troughs.length,
        breathsCount: breaths.length,
        highlightPointCount: highlightData.length,
        rawLastT,
        smoothLastT,
        xAxisMaxSecs: maxSecs,
        clipRisk:
          typeof smoothLastT === "number" &&
          typeof maxSecs === "number" &&
          smoothLastT > maxSecs,
      });
    }

    chart.update("none");
  }, [
    rawSignal,
    smoothSignal,
    peaks,
    troughs,
    breaths,
    spirometrySummary,
    selectedBreathIndex,
    showSpirometryMarkers,
    showHighlight,
    highlightFromIndex,
    highlightToIndex,
    maxSecs,
    logContext,
  ]);

  return (
    <div style={{ width: "100%", height: height }}>
      <canvas ref={chartRef} />
    </div>
  );
}
